/**
 * PipelineProgressCard — Mini horizontal pipeline flow for the Overview page.
 *
 * Shows a compact visual: Sources → Transforms → Mapping → Target Tables → Export
 * Each node shows status (completed/running/pending) derived from batch state.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Storage as SourceIcon,
  Transform as TransformIcon,
  AccountTree as MappingIcon,
  TableChart as TargetIcon,
  CloudDownload as ExportIcon,
  CheckCircle,
  HourglassEmpty,
  PlayCircleOutline,
  ErrorOutline,
} from '@mui/icons-material';

interface PipelineStage {
  label: string;
  icon: React.ReactElement;
  status: 'completed' | 'running' | 'pending' | 'failed' | 'skipped';
  detail?: string;
}

interface PipelineProgressCardProps {
  batchStatus: string;
  fileCount?: number;
  transformCount?: number;
  mappingCount?: number;
  targetTableCount?: number;
  targetSchemaName?: string;
  hasExport?: boolean;
  onClick?: (stage: string) => void;
}

const statusColors: Record<string, string> = {
  completed: '#16a34a',
  running: '#2563eb',
  pending: '#94a3b8',
  failed: '#dc2626',
  skipped: '#d1d5db',
};

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const sx = { fontSize: 14 };
  switch (status) {
    case 'completed': return <CheckCircle sx={{ ...sx, color: statusColors.completed }} />;
    case 'running': return <PlayCircleOutline sx={{ ...sx, color: statusColors.running }} />;
    case 'failed': return <ErrorOutline sx={{ ...sx, color: statusColors.failed }} />;
    default: return <HourglassEmpty sx={{ ...sx, color: statusColors.pending }} />;
  }
};

function deriveStages(props: PipelineProgressCardProps): PipelineStage[] {
  const { batchStatus, fileCount, transformCount, mappingCount, targetTableCount, targetSchemaName, hasExport } = props;
  const stages: PipelineStage[] = [];

  // 1. Sources
  const sourcesDone = !['pending'].includes(batchStatus);
  stages.push({
    label: 'Sources',
    icon: <SourceIcon sx={{ fontSize: 20 }} />,
    status: sourcesDone ? 'completed' : 'running',
    detail: fileCount ? `${fileCount} file${fileCount > 1 ? 's' : ''}` : undefined,
  });

  // 2. Transforms (optional — only show if transforms exist)
  if (transformCount != null && transformCount > 0) {
    const transformsDone = ['mapped', 'approved', 'transforming', 'completed'].includes(batchStatus);
    stages.push({
      label: 'Transforms',
      icon: <TransformIcon sx={{ fontSize: 20 }} />,
      status: transformsDone ? 'completed' : 'pending',
      detail: `${transformCount} step${transformCount > 1 ? 's' : ''}`,
    });
  }

  // 3. Mapping
  const mappingStatusMap: Record<string, PipelineStage['status']> = {
    pending: 'pending',
    profiling: 'pending',
    profiled: 'pending',
    summarizing: 'pending',
    ready_for_mapping: 'pending',
    mapping: 'running',
    mapped: 'completed',
    reviewing: 'completed',
    approved: 'completed',
    transforming: 'completed',
    loading: 'completed',
    completed: 'completed',
    failed: 'failed',
  };
  stages.push({
    label: 'Mapping',
    icon: <MappingIcon sx={{ fontSize: 20 }} />,
    status: mappingStatusMap[batchStatus] || 'pending',
    detail: mappingCount ? `${mappingCount} tables` : targetSchemaName || undefined,
  });

  // 4. Target Tables (only if mapping is done)
  if (targetTableCount && targetTableCount > 0) {
    const targetsDone = ['mapped', 'approved', 'transforming', 'completed'].includes(batchStatus);
    stages.push({
      label: 'Target Tables',
      icon: <TargetIcon sx={{ fontSize: 20 }} />,
      status: targetsDone ? 'completed' : 'pending',
      detail: `${targetTableCount} table${targetTableCount > 1 ? 's' : ''}`,
    });
  }

  // 5. Export (optional)
  if (hasExport) {
    stages.push({
      label: 'Export',
      icon: <ExportIcon sx={{ fontSize: 20 }} />,
      status: batchStatus === 'completed' ? 'completed' : 'pending',
    });
  }

  return stages;
}

const PipelineProgressCard: React.FC<PipelineProgressCardProps> = (props) => {
  const theme = useTheme();
  const stages = deriveStages(props);

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 11 }}>
          Pipeline Progress
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            overflowX: 'auto',
          }}
        >
          {stages.map((stage, i) => (
            <React.Fragment key={stage.label}>
              {/* Stage node */}
              <Box
                onClick={() => props.onClick?.(stage.label.toLowerCase().replace(/\s+/g, '_'))}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  cursor: props.onClick ? 'pointer' : 'default',
                  minWidth: 80,
                  transition: 'all 0.15s',
                  '&:hover': props.onClick ? {
                    bgcolor: alpha(statusColors[stage.status], 0.08),
                  } : {},
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(statusColors[stage.status], 0.12),
                    color: statusColors[stage.status],
                    border: `2px solid ${alpha(statusColors[stage.status], 0.3)}`,
                  }}
                >
                  {stage.icon}
                </Box>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: statusColors[stage.status] }}>
                  {stage.label}
                </Typography>
                {stage.detail && (
                  <Chip
                    label={stage.detail}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: alpha(statusColors[stage.status], 0.08),
                      color: statusColors[stage.status],
                    }}
                  />
                )}
                <StatusIcon status={stage.status} />
              </Box>

              {/* Connector arrow */}
              {i < stages.length - 1 && (
                <Box
                  sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    color: stages[i + 1].status === 'pending' ? theme.palette.divider : statusColors[stages[i + 1].status],
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 2,
                      bgcolor: 'currentColor',
                      opacity: 0.5,
                    }}
                  />
                  <Typography sx={{ fontSize: 12, ml: -0.5 }}>›</Typography>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PipelineProgressCard;
